import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Banner,
  ButtonLink,
  EmptyState,
  Pill,
  Skeleton,
  type PillStatus
} from '../primitives';

export interface InvoiceRow {
  id: string;
  number: string | null;
  status: string | null;
  total: number;
  currency: string;
  created: number;
  period_start: number | null;
  period_end: number | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

interface ListInvoicesResult {
  ok?: boolean;
  invoices?: ReadonlyArray<InvoiceRow>;
  has_more?: boolean;
  error?: string;
}

export interface InvoiceListProps {
  orgId: string;
  /** Where the "View all in web app" button points when `has_more` is true.
   *  Defaults to the Stripe customer portal-style passthrough — the page
   *  passes a deep link to a future invoices index if one ships. */
  viewAllHref?: string;
  /** How many invoices to fetch on mount (passed through to the edge function;
   *  Stripe caps this at 100). */
  limit?: number;
}

function statusToPill(status: string | null | undefined): PillStatus {
  switch ((status ?? '').toLowerCase()) {
    case 'paid':
      return 'done';
    case 'open':
    case 'draft':
      return 'progress';
    case 'uncollectible':
    case 'void':
      return 'cancel';
    default:
      return 'todo';
  }
}

function formatDate(epochSec: number): string {
  // Stripe returns UNIX seconds.
  const ms = epochSec * 1000;
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
}

function formatAmount(total: number, currency: string): string {
  // Stripe returns minor units (cents for USD).
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'USD').toUpperCase()
    }).format(total / 100);
  } catch {
    return `${(total / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/**
 * Lazily-loaded invoice list for the `/settings/billing` page. Calls the
 * `stripe-list-invoices` edge function on mount with `{org_id, limit}` and
 * renders each invoice as a row (date · amount · status · open-in-Stripe
 * link). On `has_more` an optional "View all" CTA appears beneath the list.
 *
 * Hidden by the parent for Free-tier orgs (no Stripe customer yet); this
 * component does not gate itself, so a Free-tier mount would just render an
 * empty state.
 */
export function InvoiceList({
  orgId,
  viewAllHref,
  limit = 10
}: InvoiceListProps) {
  const [rows, setRows] = useState<ReadonlyArray<InvoiceRow>>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: fnErr } = await supabase.functions.invoke(
        'stripe-list-invoices',
        { body: { org_id: orgId, limit } }
      );
      if (cancelled) return;
      if (fnErr) {
        setError("Couldn't load invoices. Please try again.");
        setLoading(false);
        return;
      }
      const result = (data ?? {}) as ListInvoicesResult;
      if (!result.ok) {
        setError("Couldn't load invoices. Please try again.");
        setLoading(false);
        return;
      }
      setRows((result.invoices ?? []) as ReadonlyArray<InvoiceRow>);
      setHasMore(result.has_more === true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, limit]);

  if (loading) {
    return (
      <div
        className="set-billing-invoices-loading"
        data-testid="billing-invoices-loading"
      >
        <Skeleton height={36} />
        <Skeleton height={36} />
        <Skeleton height={36} />
      </div>
    );
  }

  if (error) {
    return (
      <Banner kind="error" data-testid="billing-invoices-error">
        {error}
      </Banner>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState data-testid="billing-invoices-empty">
        No invoices yet. The first one will appear here after your next
        billing cycle closes.
      </EmptyState>
    );
  }

  return (
    <div
      className="set-billing-invoices"
      data-testid="billing-invoices"
    >
      <ul className="set-billing-invoices-list" role="list">
        {rows.map((inv) => {
          const href = inv.hosted_invoice_url ?? inv.invoice_pdf ?? null;
          return (
            <li
              key={inv.id}
              className="set-billing-invoice-row"
              data-testid={`billing-invoice-${inv.id}`}
              data-invoice-status={inv.status ?? ''}
            >
              <span className="set-billing-invoice-date">
                {formatDate(inv.created)}
              </span>
              <span className="set-billing-invoice-amount">
                {formatAmount(inv.total, inv.currency)}
              </span>
              <span className="set-billing-invoice-status">
                <Pill status={statusToPill(inv.status)}>
                  {inv.status ?? 'unknown'}
                </Pill>
              </span>
              <span className="set-billing-invoice-link">
                {href ? (
                  <ButtonLink
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    variant="ghost"
                    size="sm"
                    aria-label={`Open invoice ${inv.number ?? inv.id} in Stripe`}
                    data-testid={`billing-invoice-link-${inv.id}`}
                  >
                    Open ↗
                  </ButtonLink>
                ) : (
                  <span aria-hidden>—</span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
      {hasMore && viewAllHref ? (
        <div className="set-billing-invoices-more">
          <ButtonLink
            href={viewAllHref}
            target="_blank"
            rel="noreferrer noopener"
            variant="ghost"
            size="sm"
            data-testid="billing-invoices-view-all"
          >
            View all in web app ↗
          </ButtonLink>
        </div>
      ) : null}
    </div>
  );
}
